// HRMS date rules, mirrored from the experts-db backend
// (src/common/hrmsDateRules.ts) so users see the same message before
// submitting that the API would return. Keep the two files in sync.
//
// Agreed limits: leave and attendance corrections at most 30 days back,
// joining date at most 6 months ahead, employees at least 18 years old.

export const LEAVE_MAX_DAYS_BACK = 30;
export const LEAVE_MAX_DAYS_AHEAD = 365;
export const CORRECTION_MAX_DAYS_BACK = 30;
export const JOINING_MAX_MONTHS_AHEAD = 6;
export const MIN_EMPLOYEE_AGE = 18;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

const pad = (n: number) => String(n).padStart(2, "0");
const toIso = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const fromIso = (s: string) => new Date(`${s}T00:00:00Z`);

/** Today's date in India, as YYYY-MM-DD. */
export const todayIst = (now: Date = new Date()) =>
  toIso(new Date(now.getTime() + IST_OFFSET_MS));

export const addDays = (iso: string, days: number) => {
  const d = fromIso(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
};

export const addMonths = (iso: string, months: number) => {
  const d = fromIso(iso);
  d.setUTCMonth(d.getUTCMonth() + months);
  return toIso(d);
};

export const addYears = (iso: string, years: number) => addMonths(iso, years * 12);

/** A real calendar date written as YYYY-MM-DD with a 4-digit year. */
export const isValidIsoDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return toIso(fromIso(value)) === value;
};

const fmt = (iso: string) => {
  const d = fromIso(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${pad(d.getUTCDate())} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const invalid = (label: string) => `${label} is not a valid date.`;

export function validateLeaveDates(
  start: unknown,
  end: unknown,
  duration: unknown,
  today = todayIst()
): string | null {
  if (!isValidIsoDate(start)) return invalid("Start date");
  if (!isValidIsoDate(end)) return invalid("End date");
  if (end < start) return "End date cannot be before the start date.";
  if (duration === "half" && end !== start)
    return "A half-day leave must start and end on the same day.";
  const earliest = addDays(today, -LEAVE_MAX_DAYS_BACK);
  if (start < earliest)
    return `Leave can be applied at most ${LEAVE_MAX_DAYS_BACK} days back (from ${fmt(earliest)}).`;
  const latest = addDays(today, LEAVE_MAX_DAYS_AHEAD);
  if (end > latest) return `Leave can be applied at most 1 year ahead (until ${fmt(latest)}).`;
  return null;
}

export function validateCorrection(
  date: unknown,
  loginTime?: string,
  logoutTime?: string,
  today = todayIst()
): string | null {
  if (!isValidIsoDate(date)) return invalid("Date");
  if (date > today) return "Attendance can't be corrected for a future date.";
  const earliest = addDays(today, -CORRECTION_MAX_DAYS_BACK);
  if (date < earliest)
    return `Corrections are allowed for the last ${CORRECTION_MAX_DAYS_BACK} days only (from ${fmt(earliest)}).`;
  if (loginTime && Number.isNaN(Date.parse(loginTime))) return "Login time is not valid.";
  if (logoutTime && Number.isNaN(Date.parse(logoutTime))) return "Logout time is not valid.";
  if (loginTime && logoutTime && Date.parse(logoutTime) <= Date.parse(loginTime))
    return "Logout time must be after login time.";
  return null;
}

/** Profile dates; only fields that are present and non-empty are checked. */
export function validateProfileDates(
  fields: {
    dateOfJoining?: unknown;
    birthday?: unknown;
    leaveCycleStartDate?: unknown;
    nextAppraisalDueDate?: unknown;
  },
  today = todayIst()
): string | null {
  const { dateOfJoining, birthday, leaveCycleStartDate, nextAppraisalDueDate } = fields;
  if (dateOfJoining) {
    if (!isValidIsoDate(dateOfJoining)) return invalid("Date of joining");
    if (dateOfJoining < "1990-01-01") return "Date of joining is too far in the past.";
    const latest = addMonths(today, JOINING_MAX_MONTHS_AHEAD);
    if (dateOfJoining > latest)
      return `Date of joining can be at most ${JOINING_MAX_MONTHS_AHEAD} months ahead (until ${fmt(latest)}).`;
  }
  if (birthday) {
    if (!isValidIsoDate(birthday)) return invalid("Birthday");
    if (birthday < "1940-01-01") return "Birthday is too far in the past.";
    if (birthday > addYears(today, -MIN_EMPLOYEE_AGE))
      return `Employees must be at least ${MIN_EMPLOYEE_AGE} years old.`;
  }
  if (leaveCycleStartDate) {
    if (!isValidIsoDate(leaveCycleStartDate)) return invalid("Leave year start");
    if (leaveCycleStartDate < "2000-01-01" || leaveCycleStartDate > addYears(today, 2))
      return "Leave year start must be within the next 2 years.";
  }
  if (nextAppraisalDueDate) {
    if (!isValidIsoDate(nextAppraisalDueDate)) return invalid("Next appraisal due date");
    if (nextAppraisalDueDate < addYears(today, -1) || nextAppraisalDueDate > addYears(today, 3))
      return "Next appraisal due date must be between a year ago and 3 years ahead.";
  }
  return null;
}

export function validateExitDates(
  resignationDate: unknown,
  lastWorkingDay: unknown,
  relievingLetterDate?: unknown,
  today = todayIst()
): string | null {
  if (resignationDate !== undefined && resignationDate !== "") {
    if (!isValidIsoDate(resignationDate)) return invalid("Resignation date");
    if (resignationDate > addMonths(today, 6)) return "Resignation date is too far ahead.";
  }
  if (lastWorkingDay !== undefined && lastWorkingDay !== "") {
    if (!isValidIsoDate(lastWorkingDay)) return invalid("Last working day");
    if (isValidIsoDate(resignationDate) && lastWorkingDay < resignationDate)
      return "Last working day cannot be before the resignation date.";
    if (lastWorkingDay > addYears(today, 1)) return "Last working day is too far ahead.";
  }
  if (relievingLetterDate) {
    if (!isValidIsoDate(relievingLetterDate)) return invalid("Relieving letter date");
    if (isValidIsoDate(lastWorkingDay) && relievingLetterDate < lastWorkingDay)
      return "Relieving letter date cannot be before the last working day.";
  }
  return null;
}

export function validateTargetDeadline(deadline: unknown, today = todayIst()): string | null {
  if (!isValidIsoDate(deadline)) return invalid("Deadline");
  if (deadline < today) return "Deadline cannot be in the past.";
  if (deadline > addYears(today, 3)) return "Deadline is too far ahead.";
  return null;
}

export function validateHolidayDate(date: unknown, today = todayIst()): string | null {
  if (!isValidIsoDate(date)) return invalid("Date");
  const year = Number(date.slice(0, 4));
  const thisYear = Number(today.slice(0, 4));
  if (year < thisYear - 1 || year > thisYear + 2)
    return `Holiday year must be between ${thisYear - 1} and ${thisYear + 2}.`;
  return null;
}

/** min/max attributes for the HRMS date pickers, matching the rules above. */
export const hrmsDateBounds = (today = todayIst()) => {
  const year = Number(today.slice(0, 4));
  return {
    leave: { min: addDays(today, -LEAVE_MAX_DAYS_BACK), max: addDays(today, LEAVE_MAX_DAYS_AHEAD) },
    correction: { min: addDays(today, -CORRECTION_MAX_DAYS_BACK), max: today },
    joining: { min: "1990-01-01", max: addMonths(today, JOINING_MAX_MONTHS_AHEAD) },
    birthday: { min: "1940-01-01", max: addYears(today, -MIN_EMPLOYEE_AGE) },
    target: { min: today, max: addYears(today, 3) },
    holiday: { min: `${year - 1}-01-01`, max: `${year + 2}-12-31` },
    leaveCycle: { min: "2000-01-01", max: addYears(today, 2) },
    appraisal: { min: addYears(today, -1), max: addYears(today, 3) },
  };
};
